// kitchen/web/src/hooks/useClientConfig.ts
// Fetches the client KDS configuration (kdsBoardConfig) from the backend.
// Provides a default board fallback when data is not yet available. The
// server's board (in the installation's language when the venue saved none)
// wins whenever it is present; the fallback's group names follow this screen's
// language (feature 298).

import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { clientConfigApi, type ClientConfig } from '../api/client';
import { defaultKdsBoardConfig, type KdsBoardConfig } from '@shared/kds/table-board';
import { useTranslation } from '../i18n';
import { kitchenMetrics } from '../lib/sync/kitchen-metrics';
import { acquireConfigRefresh, configQueryOptions } from '../lib/sync/client-config-refresh';

export function useClientConfig(clientId: string | null): {
  config: KdsBoardConfig;
  clientConfig: ClientConfig | undefined;
  isLoading: boolean;
} {
  const { t } = useTranslation();
  const fallback = useMemo(() => defaultKdsBoardConfig(t), [t]);
  const queryClient = useQueryClient();
  // Feature 334 — one shared query, one 15 s refresh per venue for every reader
  // (cards, views): see lib/sync/client-config-refresh.ts. The venue switch
  // (feature 252) still reaches the screen within a refresh; the fast flags come
  // over the socket.
  useEffect(() => acquireConfigRefresh(queryClient, clientId), [queryClient, clientId]);
  const { data, isLoading } = useQuery<ClientConfig>(
    configQueryOptions<ClientConfig>(clientId, (id, signal) =>
      kitchenMetrics.measure('config', () => clientConfigApi.get(id, signal)),
    ),
  );

  const config: KdsBoardConfig = data?.kdsBoardConfig ?? fallback;

  return { config, clientConfig: data, isLoading };
}
